/**
 * Enumerates the stable severity tones downstream UI packages can map to their
 * own visual treatment without coupling this package to a renderer.
 *
 * @example
 * const tone = ERROR_DISPLAY_TONES.includes('danger') ? 'danger' : 'info'
 */
export const ERROR_DISPLAY_TONES = ['info', 'warning', 'danger', 'success'] as const

/**
 * Enumerates density names shared by UI packages that need compact and
 * comfortable layouts while keeping the core error package framework-free.
 *
 * @example
 * const density = ERROR_DISPLAY_DENSITIES[0]
 */
export const ERROR_DISPLAY_DENSITIES = ['compact', 'comfortable'] as const

/**
 * Narrows display severity to the values the Geenius design system expects.
 * Use this when component props accept only known error tones.
 *
 * @example
 * const tone: ErrorDisplayTone = 'warning'
 */
export type ErrorDisplayTone = (typeof ERROR_DISPLAY_TONES)[number]

/**
 * Narrows display density to the renderer-neutral choices shared by downstream
 * UI packages.
 *
 * @example
 * const density: ErrorDisplayDensity = 'compact'
 */
export type ErrorDisplayDensity = (typeof ERROR_DISPLAY_DENSITIES)[number]

/**
 * Describes one validation issue in a shape that can be rendered by web,
 * native, CLI, or logging consumers without importing a schema library.
 *
 * @example
 * const issue: ValidationIssue = { field: 'email', message: 'Email is required' }
 */
export interface ValidationIssue {
  field: string
  message: string
  code?: string
}

/**
 * Captures display-ready error metadata for packages that need consistent
 * titles, messages, and severity tones without rendering anything here.
 *
 * @example
 * const summary: NormalizedErrorSummary = {
 *   title: 'Rate limit reached',
 *   message: 'Try again later',
 *   tone: 'warning',
 *   statusCode: 429,
 * }
 */
export interface NormalizedErrorSummary {
  title: string
  message: string
  tone: ErrorDisplayTone
  statusCode?: number
  code?: string
}

/**
 * Wraps an error summary and optional validation details for reporting
 * adapters, logs, and support payloads that need a consistent envelope.
 *
 * @example
 * const envelope: ErrorReportEnvelope = {
 *   summary: { title: 'Error', message: 'Failed', tone: 'danger' },
 *   issueCount: 0,
 *   validationIssues: [],
 *   createdAt: new Date().toISOString(),
 * }
 */
export interface ErrorReportEnvelope {
  summary: NormalizedErrorSummary
  issueCount: number
  validationIssues: readonly ValidationIssue[]
  createdAt: string
  requestId?: string
}

/**
 * Configures deterministic report envelopes, especially in tests and request
 * pipelines that already know the request id.
 *
 * @example
 * const options: ErrorReportOptions = {
 *   requestId: 'req_123',
 *   now: new Date('2026-05-18T00:00:00.000Z'),
 * }
 */
export interface ErrorReportOptions {
  requestId?: string
  now?: Date
}

/**
 * Checks untrusted display input before passing it into UI props that only
 * support known Geenius error tones.
 *
 * @param value - Unknown value from props, configuration, or serialized state.
 * @returns `true` when the value is one of the supported display tones.
 *
 * @example
 * const tone = isErrorDisplayTone(input) ? input : 'danger'
 */
export function isErrorDisplayTone(value: unknown): value is ErrorDisplayTone {
  return isStringMember(ERROR_DISPLAY_TONES, value)
}

/**
 * Checks untrusted density input before a downstream UI package applies layout
 * spacing decisions.
 *
 * @param value - Unknown value from props, configuration, or serialized state.
 * @returns `true` when the value is one of the supported display densities.
 *
 * @example
 * const density = isErrorDisplayDensity(input) ? input : 'comfortable'
 */
export function isErrorDisplayDensity(value: unknown): value is ErrorDisplayDensity {
  return isStringMember(ERROR_DISPLAY_DENSITIES, value)
}

/**
 * Converts unknown thrown values into display metadata that UI packages can
 * render consistently while this package remains framework-agnostic.
 *
 * @param error - Unknown thrown value, serialized error, or plain object.
 * @param fallbackMessage - Message to use when the input does not expose a
 * non-empty message.
 * @returns A normalized title, message, tone, and optional machine metadata.
 *
 * @example
 * const summary = normalizeErrorSummary({ statusCode: 404, message: 'Missing' })
 */
export function normalizeErrorSummary(error: unknown, fallbackMessage = 'Something went wrong'): NormalizedErrorSummary {
  if (isRecord(error)) {
    const message = readString(error.message) ?? readString(error.title) ?? fallbackMessage
    const statusCode = readNumber(error.statusCode)
    const code = readString(error.code)

    return {
      title: readString(error.title) ?? titleFromStatus(statusCode),
      message,
      tone: toneFromStatus(statusCode),
      ...(statusCode === undefined ? {} : { statusCode }),
      ...(code === undefined ? {} : { code }),
    }
  }

  if (error instanceof Error) {
    return {
      title: 'Error',
      message: error.message || fallbackMessage,
      tone: 'danger',
    }
  }

  if (typeof error === 'string' && error.trim()) {
    return {
      title: 'Error',
      message: error,
      tone: 'danger',
    }
  }

  return {
    title: 'Error',
    message: fallbackMessage,
    tone: 'danger',
  }
}

/**
 * Formats validation issues for environments that need a simple newline
 * separated string, such as logs, notifications, or fallback UI.
 *
 * @param issues - Field-level validation issues in display order.
 * @returns A newline-separated summary with field names when available.
 *
 * @example
 * const text = formatValidationIssues([{ field: 'email', message: 'Required' }])
 */
export function formatValidationIssues(issues: readonly ValidationIssue[]): string {
  return issues
    .map((issue) => {
      const field = issue.field.trim()
      return field ? `${field}: ${issue.message}` : issue.message
    })
    .join('\n')
}

/**
 * Creates a transport-safe report envelope for logging and support adapters
 * that need summary metadata plus validation details.
 *
 * @param error - Unknown thrown value or serialized error to summarize.
 * @param validationIssues - Optional field-level validation issues to attach.
 * @param options - Request id and clock overrides for deterministic reports.
 * @returns A JSON-safe envelope with a normalized summary and issue metadata.
 *
 * @example
 * const report = createErrorReportEnvelope(error, [], { requestId: 'req_123' })
 */
export function createErrorReportEnvelope(
  error: unknown,
  validationIssues: readonly ValidationIssue[] = [],
  options: ErrorReportOptions = {},
): ErrorReportEnvelope {
  return {
    summary: normalizeErrorSummary(error),
    issueCount: validationIssues.length,
    validationIssues,
    createdAt: (options.now ?? new Date()).toISOString(),
    ...(options.requestId ? { requestId: options.requestId } : {}),
  }
}

/**
 * Determines whether a runtime mode may expose diagnostic details to operators
 * instead of returning production-safe generic messages.
 *
 * @param environment - Runtime environment name, usually `process.env.NODE_ENV`.
 * @returns `true` for development and test environments; otherwise `false`.
 *
 * @example
 * const includeStack = shouldExposeErrorDetails(process.env.NODE_ENV)
 */
export function shouldExposeErrorDetails(environment: string | undefined): boolean {
  return environment === 'development' || environment === 'test'
}

function titleFromStatus(statusCode: number | undefined): string {
  if (statusCode === 401 || statusCode === 403) {
    return 'Access error'
  }

  if (statusCode === 404) {
    return 'Not found'
  }

  if (statusCode === 409) {
    return 'Conflict'
  }

  if (statusCode === 429) {
    return 'Rate limit reached'
  }

  if (statusCode !== undefined && statusCode >= 500) {
    return 'Service error'
  }

  return 'Error'
}

function toneFromStatus(statusCode: number | undefined): ErrorDisplayTone {
  if (statusCode === undefined) {
    return 'danger'
  }

  if (statusCode >= 500 || statusCode === 401 || statusCode === 403) {
    return 'danger'
  }

  if (statusCode === 429 || statusCode === 409) {
    return 'warning'
  }

  if (statusCode === 404) {
    return 'info'
  }

  return 'danger'
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function isStringMember<const Values extends readonly string[]>(values: Values, value: unknown): value is Values[number] {
  return typeof value === 'string' && values.some((candidate) => candidate === value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
